import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import {
  Observable,
  combineLatest,
  concatMap,
  delay,
  filter,
  from,
  fromEvent,
  map,
  of,
  switchMap,
  take,
  toArray,
} from 'rxjs';

import { CategoryView } from 'ish-core/models/category-view/category-view.model';
import { GTMHelper } from 'ish-core/models/gtm/gtm.helper';
import { GTMEventDotCmsItems, GTMEventItems } from 'ish-core/models/gtm/gtm.model';
import { Payment } from 'ish-core/models/payment/payment.model';
import { Pricing } from 'ish-core/models/price/price.model';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { ShippingMethod } from 'ish-core/models/shipping-method/shipping-method.model';
import { getCurrentBasket } from 'ish-core/store/customer/basket';
import { loadCategory } from 'ish-core/store/shopping/categories/categories.actions';
import { getCategory, getCategoryName } from 'ish-core/store/shopping/categories/categories.selectors';
import {
  triggerBeginCheckoutGTMEvent,
  triggerPaymentMethodGTMEvent,
  triggerReviewCheckoutGTMEvent,
  triggerShippingMethodGTMEvent,
  triggeringSearchGTMEvent,
  triggeringSelectPromotionGTMEvent,
  triggeringViewItemGTMEvent,
  triggeringViewItemGTMEvents,
  triggeringViewPromotionGTMEvent,
} from 'ish-core/store/shopping/gtm/google-tag-manager.actions';
import { getProduct } from 'ish-core/store/shopping/products';

import { ProductContextFacade } from './product-context.facade';
import { ShoppingFacade } from './shopping.facade';
import { CustomDotCmsContentlet } from 'ish-core/models/dotcmscontentlet/custom-dotcms.interface';

@Injectable({ providedIn: 'root' })
export class GoogleTagManagerFacade {
  sku: string;

  constructor(
    private angulartics2GTM: Angulartics2GoogleTagManager,
    private context: ProductContextFacade,
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
    private shoppingFacade: ShoppingFacade
  ) {
    this.trackClicks();
  }

  getProductInformation(items: {}[]): Observable<GTMEventItems[]> {
    return from(items).pipe(
      map(item => {
        //@ts-ignore
        const sku = item?.productSKU || item?.sku;
        this.context.connect('sku', of(sku));
        return item;
      }),
      concatMap(item =>
        // Fetch product and price based on the SKU
        //@ts-ignore
        this.getProductForSKU(item?.productSKU || item?.sku).pipe(
          map(({ product, price, quantity }) =>
            // We assume product and price are always available as per your context handling
            GTMHelper.getItem(
              item as GTMEventDotCmsItems,
              product,
              price,
              quantity,
              this.productCategoryPath(product?.product?.defaultCategory?.categoryPath)
            )
          )
          // Ensure that the flow is completed before moving to the next item
        )
      ),
      toArray() // Collect results into an array
    );
  }

  triggeringGTMEvent(action: string, value?: {}) {
    this.angulartics2GTM.eventTrack(action, {
      event: action,
      value,
    });
  }

  pushingEmptyEventToGTM(event: string) {
    this.triggeringGTMEvent(event, {
      event,
      value: undefined,
    });
  }
  triggeringContentletEvent(event: string, items: GTMEventItems[]) {
    this.pushingEmptyEventToGTM(event);
    this.triggeringGTMEvent(event, {
      event,
      value: { ecommerce: { items } },
    });
  }

  private productCategoryPath(categories: string[]): string {
    let categoryPath: string;
    if (categories) {
      categories.forEach((pathRef, index) => {
        if (index === 0) {
          // For the first category, directly append it to the path
          categoryPath = `${pathRef}/`;
        } else {
          // Synchronously fetch the category name from the store or a cached state
          const categoryName = this.getCategoryNameSync(pathRef); // Synchronous fetch
          if (categoryName) {
            categoryPath += `${categoryName}/`;
          }
        }
      });
    }
    return categoryPath;
  }
  /* Events for view_items , view_item,search */
  triggerGTMEvent(event?: string, value?: ProductView[] | string) {
    if (event && value) {
      this.store.dispatch(triggeringViewItemGTMEvent({ event, item: value as ProductView[] }));
    } else {
      this.store.dispatch(triggeringSearchGTMEvent({ searchTerm: value as string }));
    }
  }
  //Event for Checkout

  triggerGTMPaymentEvent(payment: Payment) {
    this.store.dispatch(triggerPaymentMethodGTMEvent({ payment }));
  }
  triggerGTMShippingEvent(shipping: ShippingMethod) {
    this.store.dispatch(triggerShippingMethodGTMEvent({ shipping }));
  }
  triggerGTMEventForReviewCheckout() {
    this.store.dispatch(triggerReviewCheckoutGTMEvent());
  }
  triggerGTMEventForMultipleAddress() {
    this.store.dispatch(triggerBeginCheckoutGTMEvent());
  }

  //Event for Promotion
  selectPromotionGTMEvent(productUrl: string, contentlet: CustomDotCmsContentlet) {
    const url = productUrl;
    if (url?.startsWith('p/')) {
      this.sku = url.substring(2);
      const promotionContent: CustomDotCmsContentlet[] = [
        {
          ...contentlet,
          productSKU: this.sku,
        },
      ];
      this.store.dispatch(triggeringSelectPromotionGTMEvent({ event: 'select_promotion', item: promotionContent }));
    } else {
      const promotionContent: CustomDotCmsContentlet[] = [contentlet];
      this.store.dispatch(triggeringSelectPromotionGTMEvent({ event: 'select_promotion', item: promotionContent }));
    }
  }

  setupIntersectionObserverForGTMEvent(
    element: HTMLElement,
    items: CustomDotCmsContentlet[] | ProductView[],
    triggerCount: number,
    event: string
  ): IntersectionObserver {
    if (!SSR) {
      let currentTriggerCount = triggerCount;

      const intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && items && currentTriggerCount === 1) {
            of(undefined)
              .pipe(delay(2000))
              .subscribe(() => {
                if (event === 'view_promotion') {
                  this.store.dispatch(
                    triggeringViewPromotionGTMEvent({ event, item: items as CustomDotCmsContentlet[] })
                  );
                } else {
                  this.store.dispatch(triggeringViewItemGTMEvents({ event, item: items as ProductView[] }));
                }
              });
            currentTriggerCount++;
          }
        });
      });

      intersectionObserver.observe(element);
      return intersectionObserver;
    }
    return;
  }

  // A synchronous method to retrieve category names (assumes data is already available)
  private getCategoryNameSync(pathRef: string): string {
    let categoryName;

    // Access the store or cached data directly
    this.store.pipe(select(getCategoryName(pathRef)), take(1)).subscribe(name => {
      categoryName = name;
    });

    return categoryName;
  }
  private trackClicks() {
    // Listen for click events on the whole document
    fromEvent(this.document, 'click')
      .pipe(filter((event: Event) => this.isValidCLick(event)))
      .subscribe(() => {
        this.pushingEmptyEventToGTM('gtm.linkClick');
      });
  }

  private isValidCLick(event: Event): boolean {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A' || target.tagName === 'BUTTON') {
      return true;
    } else {
      return false;
    }
  }

  private getProductForSKU(
    sku: string
  ): Observable<{ product: { product: ProductView; category: CategoryView }; price: Pricing; quantity: number }> {
    // Connect the context to a new SKU without overriding the global context
    this.context.set('sku', () => sku);
    const product$ = this.store.pipe(select(getProduct(sku))).pipe(
      switchMap(product => {
        if (!product?.defaultCategory?.pathElements) {
          if (product?.defaultCategoryId) {
            this.store.dispatch(loadCategory({ categoryId: product.defaultCategoryId }));

            // Return a combination of observables
            return combineLatest([of(product), this.store.pipe(select(getCategory(product.defaultCategoryId)))]).pipe(
              filter(([product, category]) => product !== undefined && category !== undefined),
              map(([product, category]) => ({ product, category }))
            );
          } else {
            return of({ product, category: undefined });
          }
        } else {
          return of({ product, category: undefined });
        }
      }),
      take(1)
    );

    const price$ = this.shoppingFacade.productPrices$(sku).pipe(take(1));
    const quantity$ = this.context.select('quantity').pipe(take(1));
    // Combine product and price into one observable that returns both
    return combineLatest([product$, price$, quantity$]).pipe(
      filter(([product, price]) => product.product !== undefined && price !== undefined),
      map(([product, price, quantity]) => ({ product, price, quantity }))
    );
  }
  getItemsForBeginCheckoutGTMEvent$(): Observable<GTMEventItems[]> {
    return this.store.pipe(select(getCurrentBasket)).pipe(
      filter(basket => !!basket),
      take(1),
      switchMap(basket => this.getProductInformation(basket?.lineItems))
    );
  }
}
